import type { CartesianPose } from '@sb/geometry/CartesianPose';
import { makeNamespacedLog } from '@sb/log';
import {
  DEFAULT_WELD_TOOLTIP_ACCELERATION,
  type ArmTarget,
  type PoseArmTarget,
  type TCPOffsetOption,
} from '@sb/motion-planning';
import type {
  ArcStep,
  EquipmentStateItem,
  ExecutionConfig,
  RoutineContext,
  StepFailure,
  RoutineRunningPauseDetails,
} from '@sb/routine-runner';
import { FailureKind } from '@sb/routine-runner';
import type {
  StepAction,
  StepPlayArguments,
} from '@sb/routine-runner/Step/Step';
import Step from '@sb/routine-runner/Step/Step';
import type WaypointStep from '@sb/routine-runner/Step/Waypoint/Step';
import { getTCPLinkAndQuaternion } from '@sb/routine-runner/util/tcpFrame';
import { wait } from '@sb/utilities';
import type { NonEmptyArray } from '@sb/utilities/src/types';

import { WeldArguments } from './Arguments';
import { WeldVariables } from './Variables';

const log = makeNamespacedLog('WeldStep');

type ResumeKind = 'resume' | 'stop';

export class WeldStep extends Step<WeldArguments, WeldVariables> {
  public static areSubstepsRequired = true;

  public static Arguments = WeldArguments;

  public static Variables = WeldVariables;

  public substeps: Array<WaypointStep | ArcStep> = [];

  protected initializeVariableState(): void {
    const { completedWeldCount = 0 } = this.variablesForInitialization;

    this.variables = {
      completedWeldCount,
      currentActivity: 'none',
      approachMovementActivity: 'none',
      weldMovementActivity: 'none',
      weldOnResume: false,
    };
  }

  private motionApproach?: ReturnType<RoutineContext['doMotion']>;

  private motionWeld?: ReturnType<RoutineContext['doMotion']>;

  // Used to wait for resumption of routine after preflight check completion
  private pauseGate: {
    promise: Promise<ResumeKind>;
    resolve: (kind: ResumeKind) => void;
  } | null = null;

  private getStitchedExecutionConfig(
    weldMachine: EquipmentStateItem,
    fail: (failure: StepFailure) => void,
  ): ExecutionConfig {
    return {
      individualExecution: true,
      beforeExecutionIteration: async (iteration: number) => {
        const isStitchSegment = iteration % 2 === 0;

        if (isStitchSegment) {
          this.setVariable('currentActivity', 'startArc');
          await this.startWeldMachine({ weldMachine, fail });
          await wait(this.args.arcStartTime);
        }
      },
      afterExecutionIteration: async (iteration: number, success: boolean) => {
        const isStitchSegment = iteration % 2 === 0;

        if (isStitchSegment) {
          if (success) {
            // only fill crater if movement was successful
            this.setVariable('currentActivity', 'craterFilling');
            await wait(this.args.craterFillTime);
          }

          this.setVariable('currentActivity', 'stopArc');
          await this.stopWeldMachine({ weldMachine, fail });
        }
      },
    };
  }

  private getContinuousExecutionConfig(
    weldMachine: EquipmentStateItem,
    fail: (failure: StepFailure) => void,
  ): ExecutionConfig {
    return {
      individualExecution: false,
      beforeExecutionIteration: async () => {
        this.setVariable('currentActivity', 'startArc');
        await this.startWeldMachine({ weldMachine, fail });
        await wait(this.args.arcStartTime);
      },
      afterExecutionIteration: async (_iteration: number, success: boolean) => {
        if (success) {
          // only fill crater if movement was successful
          this.setVariable('currentActivity', 'craterFilling');
          await wait(this.args.craterFillTime);
        }

        this.setVariable('currentActivity', 'stopArc');
        await this.stopWeldMachine({ weldMachine, fail });
      },
    };
  }

  public hasSelfTimeout(): boolean {
    return false;
  }

  public async _play({
    fail,
    hasEncounteredStartStep,
    pauseRoutine,
  }: StepPlayArguments): Promise<void> {
    if (!hasEncounteredStartStep()) {
      // Skip weld steps if we haven't reached the start step
      log.info(
        '_play',
        "Skipping Weld step because we haven't reached the start step yet",
      );

      return;
    }

    const { weldMachine, torch } = this.activeEquipment;

    if (weldMachine == null) {
      fail({
        failure: {
          kind: FailureKind.WeldFailure,
        },
        failureReason: 'Weld machine not found',
      });

      return;
    }

    if (torch == null) {
      fail({
        failure: {
          kind: FailureKind.WeldFailure,
        },
        failureReason: 'Weld torch not found',
      });

      return;
    }

    this.setRoutineContextTCPOffsetOption();

    if (this.substeps.length === 0) {
      fail({
        failure: {
          kind: FailureKind.WeldFailure,
        },
        failureReason: 'No waypoints found',
      });

      return;
    }

    // Get the approach target and return early if it fails
    const [approachTarget] = (await this.getArmTargets({
      substeps: [this.substeps[0] as WaypointStep | ArcStep],
      fail,
      // Don't convert to pose arm target for approach target because user may have set shouldMatchJointAngles to true
      convertToPose: false,
    })) ?? [undefined];

    if (approachTarget === undefined) {
      return;
    }

    this.setVariable('currentActivity', 'approaching');

    const approachCompleted = await this.moveToPosition({
      fail,
      armTarget: approachTarget,
    });

    if (!approachCompleted) {
      log.warn('_play', 'Approach movement failed. Returning.', {
        variables: this.variables,
      });

      return;
    }

    // Handle preflight test run completion when approach is complete
    const shouldContinue = await this.handlePreflightPause(pauseRoutine);

    if (!shouldContinue) {
      log.info('_play', 'Preflight check not completed. Returning.');

      return;
    }

    this.setVariable('currentActivity', 'settingWeldParameters');
    await this.setWeldMachineParameters({ weldMachine, fail });

    log.info('weldingParameters', 'parameters', {
      weldParameters: this.args,
    });

    if (this.args.weaving.enabled) {
      await this.performWeavingWeld({ weldMachine, fail });
    } else if (this.args.stitching.enabled) {
      await this.performStitchedWeld({ weldMachine, fail });
    } else {
      await this.performContinuousWeld({ weldMachine, fail });
    }

    this.setVariable('currentActivity', 'none');

    this.setVariable(
      'completedWeldCount',
      this.variables.completedWeldCount + 1,
    );
  }

  /**
   * Perform a continuous weld without stitching
   */
  private async performContinuousWeld({
    weldMachine,
    fail,
  }: {
    weldMachine: EquipmentStateItem;
    fail: (failure: StepFailure) => void;
  }): Promise<void> {
    const allTargets = await this.getArmTargets({
      substeps: this.substeps as (WaypointStep | ArcStep)[],
      fail,
      convertToPose: true,
    });

    // Skip the approach target
    const targets = allTargets?.slice(1);

    if (targets === undefined) {
      log.error('performContinuousWeld', 'No targets found. Returning.');

      return;
    }

    await this.moveDuringWeld({
      targets,
      fail,
      useCartesianPlanner: true,
      executionConfig: this.getContinuousExecutionConfig(weldMachine, fail),
    });
  }

  /**
   * Perform a stitched weld, alternating between welding and not welding
   */
  private async performStitchedWeld({
    weldMachine,
    fail,
  }: {
    weldMachine: EquipmentStateItem;
    fail: (failure: StepFailure) => void;
  }): Promise<void> {
    const allTargets = await this.getArmTargets({
      substeps: this.substeps,
      fail,
      convertToPose: true,
    });

    // Skip the approach target
    const targets = allTargets?.slice(1);

    if (targets === undefined) {
      log.error('performStitchedWeld', 'No targets found. Returning.');

      return;
    }

    // Track whether the stitch pattern *logically* expects welding to be on.
    // This is persisted so pause/resume logic can make correct decisions even
    // when we're currently in a "space" (non‑welding) segment.
    // let isWelding = false;

    await this.moveDuringWeld({
      targets,
      fail,
      shouldResample: false,
      useCartesianPlanner: true,
      executionConfig: this.getStitchedExecutionConfig(weldMachine, fail),
    });
  }

  private async performWeavingWeld({
    weldMachine,
    fail,
  }: {
    weldMachine: EquipmentStateItem;
    fail: (failure: StepFailure) => void;
  }): Promise<void> {
    const allTargets = await this.getArmTargets({
      substeps: this.substeps,
      fail,
      convertToPose: true,
    });

    // Skip the approach target
    const targets = allTargets?.slice(1);

    if (targets === undefined) {
      log.error('performWeavingWeld', 'No targets found. Returning.');

      return;
    }

    // Use base path targets only; the ROS2 backend will generate the weave pattern
    await this.moveDuringWeld({
      targets,
      fail,
      shouldResample: false,
      useCartesianPlanner: true,
      executionConfig: this.getContinuousExecutionConfig(weldMachine, fail),
    });
  }

  public async _stop({ fail }: StepAction): Promise<void> {
    log.info('_stop', 'Stopping Weld step');

    this.resolvePauseGate('stop'); // ensure no pending waits

    const { weldMachine } = this.activeEquipment;

    if (weldMachine == null) {
      log.info('_stop', 'No weld machine found. Nothing to stop.');

      return;
    }

    this.setVariable('currentActivity', 'stopArc');
    await this.stopWeldMachine({ weldMachine, fail });

    if (this.motionApproach != null) {
      this.motionApproach.emit('cancel');
    }

    if (this.motionWeld != null) {
      this.motionWeld.emit('cancel');
    }

    this.setVariable('currentActivity', 'none');
  }

  public async _pause({ fail }: StepAction): Promise<void> {
    log.info('_pause', 'Pausing Weld step');

    const { weldMachine } = this.activeEquipment;

    if (weldMachine == null) {
      log.info('_pause', 'No weld machine found. Nothing to pause.');

      return;
    }

    await this.stopWeldMachine({ weldMachine, fail, keepState: true });

    if (this.motionApproach != null) {
      this.motionApproach.emit('pause');
    }

    if (this.motionWeld != null) {
      this.motionWeld.emit('pause');
    }
  }

  public async _resume({ fail }: StepAction): Promise<void> {
    log.info('_resume', 'Resuming Weld step');

    this.resolvePauseGate('resume'); // unblock _play() waiting for resume

    const { weldMachine } = this.activeEquipment;

    if (weldMachine == null) {
      log.info('_resume', 'No weld machine found. Nothing to resume.');

      return;
    }

    // Main question: do we need to be starting weld machine again once approach is complete?
    if (this.motionApproach != null) {
      this.motionApproach.emit('resume');
      this.setVariable('approachMovementActivity', 'moving');
    }

    if (this.motionWeld != null) {
      // If we were welding when paused
      if (this.variables.weldOnResume) {
        this.setVariable('currentActivity', 'startArc');
        await this.startWeldMachine({ weldMachine, fail });
      }

      this.motionWeld.emit('resume');
      this.setVariable('weldMovementActivity', 'moving');
      this.setVariable('currentActivity', 'movingDuringWeld');
    }
  }

  private get activeEquipment(): {
    weldMachine: EquipmentStateItem | undefined;
    torch: EquipmentStateItem | undefined;
  } {
    const allEquipment = this.routineContext.equipment.getEquipmentState();

    const weldMachine = allEquipment.find(
      (equipment) => equipment.id === this.args.selectedMachineID,
    );

    const torch = allEquipment.find(
      (equipment) => equipment.id === this.args.selectedTorchID,
    );

    return { weldMachine, torch };
  }

  private getTCPOffsetOption(): TCPOffsetOption {
    const { torch } = this.activeEquipment;

    if (torch == null) {
      throw new Error('Torch not found');
    }

    if (this.args.selectedTorchID == null) {
      throw new Error('Selected torch ID is null');
    }

    // TCP Offset option a bit of a hack. May need to revisit the indexing once we add dedicated torches, not just custom grippers.
    // See https://github.com/standardbots/sb/blob/540d718e5d2bbe3226ea9b4128b9f49392bfc6ef/libs/integrations/implementations/CustomGripper/frontend.tsx#L30
    return `ee-${torch.state.kind}-0`;
  }

  private setRoutineContextTCPOffsetOption(): void {
    const tcpOffset = this.getTCPOffsetOption();
    this.routineContext.setTCPOffsetOption(tcpOffset);
  }

  private async moveToPosition({
    armTarget,
    fail,
  }: {
    armTarget: ArmTarget;
    fail: (failure: StepFailure) => void;
    useCartesianPlanner?: boolean;
  }): Promise<boolean> {
    // Weld does not observe push mode: should not be pushing up against object.
    // Also, ros2 does not support push at this time (2025-02-19).
    let completed = false;

    const motion = this.routineContext.doMotion({
      request: {
        targets: [armTarget] as NonEmptyArray<ArmTarget>,
      },
      speedProfiles: [this.args.approachSpeedProfile],
      pushUntilCollision: false,
      pushMode: false,
      stepID: this.id,
    });

    motion.on('requestingPlan', () => {
      // Only checking previous state for first few states because we could be in a play/pause/resume flow.
      if (this.variables.approachMovementActivity === 'none') {
        this.setVariable('approachMovementActivity', 'requestingPlan');
      }
    });

    motion.on('planning', () => {
      if (this.variables.approachMovementActivity === 'requestingPlan') {
        this.setVariable('approachMovementActivity', 'planning');
      }
    });

    motion.on('beginMotion', () => {
      this.setVariable('approachMovementActivity', 'moving');
    });

    motion.on('pause', () => {
      this.setVariable('approachMovementActivity', 'paused');
    });

    motion.on('complete', () => {
      log.debug('moveToApproachPosition', 'Approach motion complete');

      completed = true;
    });

    motion.on('cancelled', () => {
      this.setVariable('approachMovementActivity', 'none');
    });

    motion.on('failure', (failure) => {
      this.setVariable('approachMovementActivity', 'none');
      fail(failure);
    });

    this.motionApproach = motion;

    try {
      await motion.race('failure', 'complete', 'cancelled');
    } finally {
      this.setVariable('approachMovementActivity', 'none');
      motion.removeAllListeners();

      delete this.motionApproach;
    }

    return completed;
  }

  /**
   * Handles movement one set of targets at a time.
   */
  private async moveDuringWeld({
    targets,
    fail,
    shouldResample = false,
    useCartesianPlanner = false,
    executionConfig,
  }: {
    targets: ArmTarget[];
    fail: (failure: StepFailure) => void;
    shouldResample?: boolean;
    useCartesianPlanner?: boolean;
    executionConfig?: ExecutionConfig;
  }): Promise<boolean> {
    let completed = false;

    if (targets.length === 0) {
      log.debug('moveDuringWeld', 'No targets found. Returning.');

      return completed;
    }

    // Get effective TCP option from selected torch
    const effectiveTCPOption = this.getTCPOffsetOption();

    log.info('moveDuringWeld', `Using torch TCP option:`, {
      effectiveTCPOption,
      selectedTorchID: this.args.selectedTorchID,
    });

    // Convert tcpOption to ROS link name for cartesian planning
    const { equipmentItems } = this.routineContext.equipment;

    const tcpResult = getTCPLinkAndQuaternion(
      equipmentItems,
      effectiveTCPOption,
    );

    const toolFrame = tcpResult?.tcpLink ?? 'tool0'; // Fallback to flange

    log.info(
      'moveDuringWeld',
      `Tooltip selection: tcpOption='${effectiveTCPOption}' → toolFrame='${toolFrame}'`,
    );

    // Weld does not observe push mode: should not be pushing up against object.
    // Also, ros2 does not support push at this time (2025-02-19).
    const motion = this.routineContext.doMotion({
      request: {
        targets: targets as NonEmptyArray<ArmTarget>,
        useCartesianPlanner,
        toolFrame, // Add tool frame selection
        weaveParameters: this.args.weaving,
        stitchParameters: {
          ...this.args.stitching,
          spaceTravelSpeed:
            this.args.stitching.spaceSpeedMultiplier * this.args.travelSpeed,
          spaceTravelAcceleration:
            // use max tooltip acceleration from weld travel speed profile
            this.args.weldTravelSpeedProfile.maxTooltipAcceleration ??
            DEFAULT_WELD_TOOLTIP_ACCELERATION,
        },
      },
      speedProfiles: targets.map(() => this.args.weldTravelSpeedProfile),
      pushUntilCollision: false,
      pushMode: false,
      stepID: this.id,
      shouldResample,
      executionConfig,
    });

    motion.on('requestingPlan', () => {
      if (this.variables.weldMovementActivity === 'none') {
        this.setVariable('weldMovementActivity', 'requestingPlan');
      }
    });

    motion.on('planning', () => {
      if (this.variables.weldMovementActivity === 'requestingPlan') {
        this.setVariable('weldMovementActivity', 'planning');
      }
    });

    motion.on('beginMotion', () => {
      this.setVariable('weldMovementActivity', 'moving');
    });

    motion.on('pause', () => {
      this.setVariable('weldMovementActivity', 'paused');
    });

    motion.on('complete', () => {
      log.debug('moveDuringWeld', 'Welding motion complete');

      completed = true;
    });

    motion.on('cancelled', () => {
      this.setVariable('weldMovementActivity', 'none');
    });

    motion.on('failure', (failure) => {
      this.setVariable('weldMovementActivity', 'none');
      fail(failure);
    });

    this.motionWeld = motion;

    try {
      await motion.race('failure', 'complete', 'cancelled');
    } finally {
      this.setVariable('weldMovementActivity', 'none');
      motion.removeAllListeners();

      delete this.motionWeld;
    }

    return completed;
  }

  /**
   * Stop the current weld machine.
   *
   * Weld stop commands may be sent even if welding hasn't started yet.
   */
  private async stopWeldMachine({
    weldMachine,
    fail,
    keepState = false,
  }: {
    weldMachine: EquipmentStateItem;
    fail: (failure: StepFailure) => void;
    keepState?: boolean;
  }): Promise<void> {
    let failureReason: string;

    if (!keepState) {
      this.setVariable('weldOnResume', false);
    }

    switch (weldMachine.kind) {
      case 'MillerWeldMachine':
        await this.stopMillerWeldMachine({ fail });
        this.setVisualizerWeldArcFlashEffect(false);

        break;
      case 'EsabWeldMachine':
        failureReason = 'ESAB weld machine not supported';

        fail({
          failure: {
            kind: FailureKind.WeldFailure,
          },
          failureReason,
        });

        break;
      case 'WeldMachine':
        failureReason = 'Generic weld machine not supported';

        fail({
          failure: {
            kind: FailureKind.WeldFailure,
          },
          failureReason,
        });

        break;

      default:
        failureReason = `Unknown weld machine type: ${weldMachine.kind}`;

        fail({
          failure: {
            kind: FailureKind.WeldFailure,
          },
          failureReason,
        });

        break;
    }
  }

  private async startWeldMachine({
    weldMachine,
    fail,
  }: {
    weldMachine: EquipmentStateItem;
    fail: (failure: StepFailure) => void;
  }): Promise<void> {
    let failureReason: string;

    this.setVariable('weldOnResume', true);

    switch (weldMachine.kind) {
      case 'MillerWeldMachine':
        try {
          await this.startMillerWeldMachine({ fail });
        } catch (error) {
          fail({
            failure: { kind: FailureKind.WeldFailure },
            failureReason: `Failed to start weld machine: ${error.message}`,
            error,
          });
        }

        this.setVisualizerWeldArcFlashEffect(true);
        break;
      case 'EsabWeldMachine':
        failureReason = 'ESAB weld machine not supported';

        fail({
          failure: {
            kind: FailureKind.WeldFailure,
          },
          failureReason,
        });

        break;
      case 'WeldMachine':
        failureReason = 'Generic weld machine not supported';

        fail({
          failure: {
            kind: FailureKind.WeldFailure,
          },
          failureReason,
        });

        break;
      default:
        failureReason = `Unknown weld machine type: ${weldMachine.kind}`;

        fail({
          failure: {
            kind: FailureKind.WeldFailure,
          },
          failureReason,
        });

        break;
    }
  }

  private async setWeldMachineParameters({
    weldMachine,
    fail,
  }: {
    weldMachine: EquipmentStateItem;
    fail: (failure: StepFailure) => void;
  }): Promise<void> {
    let failureReason: string;

    switch (weldMachine.kind) {
      case 'MillerWeldMachine':
        await this.setMillerWeldParameters({ fail });
        break;
      case 'EsabWeldMachine':
        failureReason = 'ESAB weld machine not supported';

        fail({
          failure: {
            kind: FailureKind.WeldFailure,
          },
          failureReason,
        });

        break;
      case 'WeldMachine':
        failureReason = 'Generic weld machine not supported';

        fail({
          failure: {
            kind: FailureKind.WeldFailure,
          },
          failureReason,
        });

        break;
      default:
        failureReason = `Unknown weld machine type: ${weldMachine.kind}`;

        fail({
          failure: {
            kind: FailureKind.WeldFailure,
          },
          failureReason,
        });

        break;
    }
  }

  private async stopMillerWeldMachine({
    fail,
  }: {
    fail: (failure: StepFailure) => void;
  }): Promise<void> {
    try {
      await this.routineContext.equipment.executeCommand({
        kind: 'MillerWeldMachineCommand',
        deviceID: this.args.selectedMachineID,
        subCommand: {
          kind: 'stopArc',
        },
      });
    } catch (error) {
      fail({
        failure: { kind: FailureKind.WeldFailure },
        failureReason: `Failed to stop weld machine: ${error.message}`,
        error,
      });
    }
  }

  private async startMillerWeldMachine({
    fail,
  }: {
    fail: (failure: StepFailure) => void;
  }): Promise<void> {
    try {
      await this.routineContext.equipment.executeCommand({
        kind: 'MillerWeldMachineCommand',
        deviceID: this.args.selectedMachineID,
        subCommand: {
          kind: 'startArc',
        },
      });
    } catch (error) {
      fail({
        failure: { kind: FailureKind.WeldFailure },
        failureReason: `Failed to start weld machine: ${error.message}`,
        error,
      });
    }
  }

  private async setMillerWeldParameters({
    fail,
  }: {
    fail: (failure: StepFailure) => void;
  }): Promise<void> {
    try {
      await this.routineContext.equipment.executeCommand({
        kind: 'MillerWeldMachineCommand',
        deviceID: this.args.selectedMachineID,
        subCommand: {
          kind: 'setWeldParameters',
          weldTravelSpeed: 0, // This is disregarded by machine. Just set it like this for now.
          ...this.args.millerWeldParameters,
        },
      });
    } catch (error) {
      fail({
        failure: { kind: FailureKind.WeldFailure },
        failureReason: `Failed to set weld machine parameters: ${error.message}`,
        error,
      });
    }
  }

  /**
   * Get the arm targets for the given substeps.
   *
   * Optionally converts joint arm targets to pose arm targets.
   *
   * Fancy typing ensures the output array length matches the input array length, and enforces
   * the conversion to pose arm targets if requested.
   */
  private async getArmTargets<
    T extends Array<WaypointStep | ArcStep>,
    P extends boolean = true,
  >({
    substeps,
    fail,
    convertToPose = true as P,
  }: {
    substeps: T;
    fail: (failure: StepFailure) => void;
    convertToPose?: P;
  }): Promise<
    | { [K in keyof T]: P extends true ? ArmTarget & PoseArmTarget : ArmTarget }
    | undefined
  > {
    const targets: ArmTarget[] = [];

    const { kinematicState } = this.routineContext.getRoutineRunnerState();

    let previousTargetPose: CartesianPose =
      kinematicState.tcpOffsetOption === 'wrist'
        ? kinematicState.wristPose
        : kinematicState.tooltipPoint;

    for (let i = 0; i < substeps.length; i += 1) {
      const substep: WaypointStep | ArcStep = substeps[i];

      if (
        substep.getStepKind() !== 'Waypoint' &&
        substep.getStepKind() !== 'Arc'
      ) {
        // This is a dev error. Will need to update once we add additional flourishes.
        throw new Error(
          `Substep is not a waypoint or arc: kind=${substep.getStepKind()}`,
        );
      }

      const result = await substep.getArmTarget({
        effectiveTCPOption: this.getTCPOffsetOption(),
        parentCompletedCount: this.variables.completedWeldCount,
        previousTargetPose,
      });

      if ('failure' in result) {
        fail(result);

        return undefined;
      }

      const maybeTargetArray = result.target;
      const { poseForPreviousTarget } = result;

      previousTargetPose = poseForPreviousTarget;

      const targetArray = Array.isArray(maybeTargetArray)
        ? maybeTargetArray
        : [maybeTargetArray];

      for (let target of targetArray) {
        // Only convert to pose if requested
        if (convertToPose && !('pose' in target)) {
          target = {
            ...target,
            // this is a joint arm target so lets convert it to a pose arm target
            pose: await this.routineContext.getTargetPose(target),
          };

          if ('jointAngles' in target) {
            // remove jointAngles from target otherwise motion planner will use them
            delete target.jointAngles;
          }
        }

        targets.push(target);
      }
    }

    return targets as {
      [K in keyof T]: P extends true ? ArmTarget & PoseArmTarget : ArmTarget;
    };
  }

  // Enable/disable the weld flash effect on the torch in visualizer
  private setVisualizerWeldArcFlashEffect(isWelding: boolean) {
    // Get current weld machine state
    const { weldMachine } = this.activeEquipment;

    // TODO: fix the return type of activeEquipment.weldMachine to
    // specify that these are weld machine state items (then we don't need to check for 'isWelding' in the state)
    if (weldMachine && 'isWelding' in weldMachine.state) {
      weldMachine.state.isWelding = isWelding;
    }
  }

  /**
   * Handle preflight test run completion - pause the routine if in preflight mode.
   * When pausing, waits for resume/stop to be called.
   * Returns true if:
   *  - preflight check was completed and routine was resumed, OR
   *  - preflight was not active
   * Returns false if:
   *  - routine was stopped while paused for preflight check completion
   */
  private async handlePreflightPause(
    pauseRoutine: (pauseDetails: RoutineRunningPauseDetails) => Promise<void>,
  ): Promise<boolean> {
    log.info('checkPreflightComplete', 'checking preflight completion');

    const routineRunnerState = this.routineContext.getRoutineRunnerState();

    if (
      routineRunnerState.kind === 'RoutineRunning' &&
      routineRunnerState.isPreflightTestRun
    ) {
      log.info('checkPreflightComplete', 'preflight test run completed');

      // Set isPreflightTestRun to false
      this.routineContext.setRoutineRunnerState({
        ...routineRunnerState,
        isPreflightTestRun: false,
        readyToBeginMotion: true,
        shouldCheckGuidedMode: false,
        shouldNextArmMoveBeGuidedMode: false,
      });

      // Trigger pause for preflight test run completion
      await pauseRoutine({
        kind: 'preflightTestRunCompleted',
        reason: 'The jogging motion completed.',
      });

      const kind = await this.waitForResume();

      if (kind === 'stop') {
        // routine was stopped, so we return false
        return false;
      }

      // routine was resumed, so we return true
      return true;
    }

    log.info('checkPreflightComplete', 'preflight is not active');

    // preflight was not active, so we return true
    return true;
  }

  private createPauseGate(): void {
    if (this.pauseGate) return;

    let resolve!: (kind: ResumeKind) => void;

    const promise = new Promise<ResumeKind>((r) => {
      resolve = r;
    });

    this.pauseGate = { promise, resolve };
  }

  private async waitForResume(): Promise<ResumeKind> {
    this.createPauseGate();

    const kind = await this.pauseGate!.promise;
    this.pauseGate = null;

    return kind;
  }

  private resolvePauseGate(kind: ResumeKind): void {
    if (!this.pauseGate) return;

    this.pauseGate.resolve(kind);
    this.pauseGate = null;
  }
}
